/**
 * ShareModal Component
 * Modal for sharing shopping lists via text (default) or link
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Link as LinkIcon, 
  Copy, 
  Check,
  Share2,
  Mail,
  MessageCircle,
  MessageSquare,
  FileText
} from 'lucide-react';
import toast from 'react-hot-toast';
import { shareAPI } from '@/services/api';

const SHARE_TYPE_OPTIONS = [
  {
    value: 'text',
    label: 'Share as text',
    description: 'Recommended — WhatsApp/SMS friendly',
    icon: FileText
  },
  {
    value: 'link',
    label: 'Share as link',
    description: 'Read-only link for browsers',
    icon: LinkIcon
  }
];

const SHARE_MODE_OPTIONS = [
  {
    value: 'readOnly',
    label: 'View only',
    helper: 'Recipients can view but not edit'
  },
  {
    value: 'collaborative',
    label: 'Collaborate',
    helper: 'Future-ready for shared editing'
  },
  {
    value: 'linkOnly',
    label: 'Link only',
    helper: 'Generate a private link'
  }
];

export default function ShareModal({ isOpen, onClose, listId, listName = 'Shopping List', items = [] }) {
  const [shareType, setShareType] = useState('text');
  const [shareUrl, setShareUrl] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedText, setCopiedText] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);
  const [shareMode, setShareMode] = useState('readOnly');
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [error, setError] = useState('');
  
  const hasListContext = Boolean(listId);

  const shareText = useMemo(() => {
    if (!items || items.length === 0) {
      return '🛒 ' + listName + '\n\n(No items yet)';
    }
    const activeItems = items.filter(item => !item.completed);
    const completedItems = items.filter(item => item.completed);
    let text = '🛒 ' + listName + '\n\n';
    if (activeItems.length > 0) {
      text += activeItems.map(item => {
        let line = '• ' + item.name;
        if (item.quantity && item.quantity > 1) line += ' (×' + item.quantity + ')';
        if (item.price) line += ' — £' + Number(item.price).toFixed(2);
        return line;
      }).join('\n');
    }
    if (includeCompleted && completedItems.length > 0) {
      if (activeItems.length > 0) text += '\n\n';
      text += '✓ Completed:\n';
      text += completedItems.map(item => {
        let line = '  ✓ ' + item.name;
        if (item.quantity && item.quantity > 1) line += ' (×' + item.quantity + ')';
        return line;
      }).join('\n');
    }
    text += '\n\n— Shared via FinList';
    return text;
  }, [items, listName, includeCompleted]);

  const normalizeShareUrl = (url) => {
    if (!url) return url;
    try {
      const parsed = new URL(url, window.location.origin);
      if (parsed.hostname === 'localhost') {
        parsed.protocol = 'http:';
        parsed.port = window.location.port || parsed.port;
        return parsed.toString();
      }
      return parsed.toString();
    } catch { return url; }
  };

  const handleCreateShare = useCallback(async () => {
    if (!isOpen || !listId) return;
    setIsCreating(true);
    setShareUrl('');
    setCopied(false);
    setExpiresAt(null);
    setError('');
    try {
      const payload = { shareMode, includeCompleted, expiresInDays: 7, listName };
      const shareResponse = await shareAPI.createShareForList(listId, payload);
      const shareData = shareResponse?.data ?? shareResponse;
      if (!shareData?.shareUrl) throw new Error('The server did not return a shareable link.');
      const { shareUrl: generatedUrl, expiresAt: expires } = shareData;
      if (isOpen) {
        setShareUrl(normalizeShareUrl(generatedUrl));
        if (expires) setExpiresAt(new Date(expires));
        toast.success('Shareable link created!');
      }
    } catch (err) {
      console.error('Failed to create share:', err);
      const errorMessage = err?.response?.data?.error?.message || err?.response?.data?.error || err?.message || 'Failed to create shareable link';
      const isNetworkError = !err?.response;
      if (isNetworkError && typeof window !== 'undefined' && listId) {
        const fallbackUrl = window.location.origin + '/shared/' + listId + '?mode=' + (shareMode || 'readOnly');
        setShareUrl(normalizeShareUrl(fallbackUrl));
        setExpiresAt(null);
        toast.success('Offline fallback link generated.');
      } else { setShareUrl(''); }
      if (isOpen) setError(errorMessage);
      if (!isNetworkError) toast.error(errorMessage);
    } finally { setIsCreating(false); }
  }, [includeCompleted, isOpen, listId, listName, shareMode]);

  useEffect(() => {
    if (isOpen && hasListContext && shareType === 'link' && !shareUrl && !isCreating) {
      handleCreateShare();
    }
  }, [isOpen, hasListContext, shareType, shareUrl, isCreating, handleCreateShare]);

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopiedText(true);
      toast.success('List copied to clipboard!');
      setTimeout(() => setCopiedText(false), 2000);
    } catch { toast.error('Failed to copy text'); }
  };

  const handleShareText = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: listName, text: shareText });
        toast.success('Shared successfully!');
      } catch (err) { if (err.name !== 'AbortError') handleCopyText(); }
    } else { handleCopyText(); }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Failed to copy link'); }
  };

  const handleWhatsAppShareText = () => {
    window.open('https://wa.me/?text=' + encodeURIComponent(shareText), '_blank');
  };
  const handleSMSShareText = () => {
    window.location.href = 'sms:?body=' + encodeURIComponent(shareText);
  };
  const handleEmailShareText = () => {
    const subject = 'Shopping List: ' + listName;
    window.location.href = 'mailto:?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(shareText);
  };

  const handleWhatsAppShare = () => {
    const message = 'Check out my shopping list:\nView it here → ' + shareUrl;
    window.open('https://wa.me/?text=' + encodeURIComponent(message), '_blank');
  };
  const handleSMSShare = () => {
    const message = 'Check out my shopping list:\nView it here → ' + shareUrl;
    window.location.href = 'sms:?body=' + encodeURIComponent(message);
  };
  const handleEmailShare = () => {
    const subject = 'Shopping List: ' + listName;
    const body = 'Check out my shopping list:\n\nView it here:\n' + shareUrl + '\n\nNote: This link will expire in 7 days.';
    window.location.href = 'mailto:?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body);
  };

  const handleClose = () => {
    setShareUrl('');
    setCopied(false);
    setCopiedText(false);
    setExpiresAt(null);
    setShareMode('readOnly');
    setIncludeCompleted(false);
    setShareType('text');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/30 backdrop-blur-md z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.15)] border border-gray-100/80 dark:border-slate-700/70 overflow-hidden max-h-[90vh] overflow-y-auto"
            >
              <div className="relative px-6 pt-6 pb-4">
                <button onClick={handleClose} className="absolute right-4 top-4 p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition-all duration-200 active:scale-95">
                  <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </button>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg shadow-emerald-500/30">
                    <Share2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-50">Share List</h2>
                  </div>
                </div>
              </div>

              <div className="px-6 pb-6 space-y-4">
                {hasListContext ? (
                  <>
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">Share method</label>
                      <div className="grid grid-cols-2 gap-2">
                        {SHARE_TYPE_OPTIONS.map((type) => {
                          const isActive = shareType === type.value;
                          const Icon = type.icon;
                          return (
                            <button key={type.value} type="button" onClick={() => setShareType(type.value)}
                              className={`rounded-2xl border px-3 py-3 text-left transition-all ${isActive ? 'border-emerald-400 bg-emerald-50/70 text-emerald-900 shadow-sm dark:bg-emerald-500/10 dark:text-emerald-100' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-slate-700 dark:bg-slate-800 dark:text-gray-200 dark:hover:border-slate-500'}`}>
                              <div className="flex items-center gap-2 text-sm font-semibold">
                                <Icon className="w-4 h-4" />
                                <span>{type.label}</span>
                              </div>
                              <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">{type.description}</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800 px-4 py-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Include completed</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Show purchased items</p>
                        </div>
                        <button type="button" onClick={() => setIncludeCompleted((prev) => !prev)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${includeCompleted ? 'bg-emerald-500' : 'bg-gray-300'}`}>
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${includeCompleted ? 'translate-x-5' : 'translate-x-1'}`} />
                        </button>
                      </div>
                    </div>

                    {shareType === 'text' && (
                      <>
                        <div className="space-y-2">
                          <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">Preview</label>
                          <div className="rounded-2xl border border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-800 p-3 max-h-40 overflow-y-auto">
                            <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans leading-relaxed">{shareText}</pre>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <button onClick={handleShareText} className="w-full px-6 py-3.5 bg-gradient-to-br from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 active:scale-[0.98] text-white font-semibold rounded-2xl transition-all duration-200 shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 flex items-center justify-center gap-2">
                            <Share2 className="w-5 h-5" />
                            <span>Share now</span>
                          </button>
                          <button onClick={handleCopyText} className="w-full px-4 py-2.5 bg-white hover:bg-gray-50 active:scale-[0.98] border border-gray-200 dark:border-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200 font-medium rounded-2xl transition-all duration-200 flex items-center justify-center gap-2">
                            {copiedText ? (<><Check className="w-4 h-4 text-emerald-500" /><span>Copied!</span></>) : (<><Copy className="w-4 h-4" /><span>Copy text</span></>)}
                          </button>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2 uppercase tracking-wide">Or share via</label>
                          <div className="grid grid-cols-3 gap-2">
                            <button onClick={handleWhatsAppShareText} className="flex flex-col items-center gap-2 p-3 bg-white hover:bg-gray-50 active:scale-95 border border-gray-200 rounded-2xl transition-all duration-200 shadow-sm dark:bg-slate-800 dark:border-slate-700 dark:hover:bg-slate-700">
                              <div className="p-2 bg-green-500 text-white rounded-xl"><MessageCircle className="w-4 h-4" /></div>
                              <span className="text-[10px] font-semibold text-gray-700 dark:text-gray-100">WhatsApp</span>
                            </button>
                            <button onClick={handleSMSShareText} className="flex flex-col items-center gap-2 p-3 bg-white hover:bg-gray-50 active:scale-95 border border-gray-200 rounded-2xl transition-all duration-200 shadow-sm dark:bg-slate-800 dark:border-slate-700 dark:hover:bg-slate-700">
                              <div className="p-2 bg-blue-500 text-white rounded-xl"><MessageSquare className="w-4 h-4" /></div>
                              <span className="text-[10px] font-semibold text-gray-700 dark:text-gray-100">SMS</span>
                            </button>
                            <button onClick={handleEmailShareText} className="flex flex-col items-center gap-2 p-3 bg-white hover:bg-gray-50 active:scale-95 border border-gray-200 rounded-2xl transition-all duration-200 shadow-sm dark:bg-slate-800 dark:border-slate-700 dark:hover:bg-slate-700">
                              <div className="p-2 bg-purple-500 text-white rounded-xl"><Mail className="w-4 h-4" /></div>
                              <span className="text-[10px] font-semibold text-gray-700 dark:text-gray-100">Email</span>
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    {shareType === 'link' && (
                      <>
                        <div className="space-y-2">
                          <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">Access level</label>
                          <div className="grid grid-cols-1 gap-2">
                            {SHARE_MODE_OPTIONS.map((mode) => {
                              const isActive = shareMode === mode.value;
                              return (
                                <button key={mode.value} type="button" onClick={() => { setShareMode(mode.value); setShareUrl(''); }}
                                  className={`w-full rounded-2xl border px-4 py-3 text-left transition-all ${isActive ? 'border-emerald-400 bg-emerald-50/70 text-emerald-900 shadow-sm dark:bg-emerald-500/10 dark:text-emerald-100' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-slate-700 dark:bg-slate-800 dark:text-gray-200 dark:hover:border-slate-500'}`}>
                                  <div className="flex items-center justify-between text-sm font-semibold">
                                    <span>{mode.label}</span>
                                    {isActive && <Check className="h-4 w-4" />}
                                  </div>
                                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{mode.helper}</p>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {isCreating ? (
                          <div className="text-center py-8">
                            <div className="inline-block w-8 h-8 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 font-medium">Creating link...</p>
                          </div>
                        ) : shareUrl ? (
                          <>
                            <button onClick={handleCopyLink} className="w-full px-6 py-3.5 bg-gradient-to-br from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 active:scale-[0.98] text-white font-semibold rounded-2xl transition-all duration-200 shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 flex items-center justify-center gap-2">
                              {copied ? (<><Check className="w-5 h-5" /><span>Link copied!</span></>) : (<><Copy className="w-5 h-5" /><span>Copy link</span></>)}
                            </button>
                            {expiresAt && <p className="text-xs text-gray-400 dark:text-gray-500 text-center">Expires {expiresAt.toLocaleDateString()} at {expiresAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>}
                            <div>
                              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2 uppercase tracking-wide">Share via</label>
                              <div className="grid grid-cols-3 gap-2">
                                <button onClick={handleWhatsAppShare} className="flex flex-col items-center gap-2 p-3 bg-white hover:bg-gray-50 active:scale-95 border border-gray-200 rounded-2xl transition-all duration-200 shadow-sm dark:bg-slate-800 dark:border-slate-700 dark:hover:bg-slate-700">
                                  <div className="p-2 bg-green-500 text-white rounded-xl"><MessageCircle className="w-4 h-4" /></div>
                                  <span className="text-[10px] font-semibold text-gray-700 dark:text-gray-100">WhatsApp</span>
                                </button>
                                <button onClick={handleSMSShare} className="flex flex-col items-center gap-2 p-3 bg-white hover:bg-gray-50 active:scale-95 border border-gray-200 rounded-2xl transition-all duration-200 shadow-sm dark:bg-slate-800 dark:border-slate-700 dark:hover:bg-slate-700">
                                  <div className="p-2 bg-blue-500 text-white rounded-xl"><MessageSquare className="w-4 h-4" /></div>
                                  <span className="text-[10px] font-semibold text-gray-700 dark:text-gray-100">SMS</span>
                                </button>
                                <button onClick={handleEmailShare} className="flex flex-col items-center gap-2 p-3 bg-white hover:bg-gray-50 active:scale-95 border border-gray-200 rounded-2xl transition-all duration-200 shadow-sm dark:bg-slate-800 dark:border-slate-700 dark:hover:bg-slate-700">
                                  <div className="p-2 bg-purple-500 text-white rounded-xl"><Mail className="w-4 h-4" /></div>
                                  <span className="text-[10px] font-semibold text-gray-700 dark:text-gray-100">Email</span>
                                </button>
                              </div>
                            </div>
                            <div className="bg-blue-50/50 dark:bg-slate-800 border border-blue-100 dark:border-slate-700 rounded-2xl p-3">
                              <div className="flex gap-2.5">
                                <LinkIcon className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                                <div className="text-xs text-gray-700 dark:text-gray-100">
                                  <p className="font-semibold mb-1">View-only sharing</p>
                                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">Recipients can view without an account. Link expires in 7 days.</p>
                                </div>
                              </div>
                            </div>
                          </>
                        ) : error ? (
                          <div className="text-center py-6 space-y-3">
                            <p className="text-sm text-rose-600 dark:text-rose-400 font-semibold">{error}</p>
                            <button onClick={handleCreateShare} className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white text-sm font-medium rounded-xl transition-all duration-200 shadow-sm">Try Again</button>
                          </div>
                        ) : null}
                      </>
                    )}
                  </>
                ) : (
                  <div className="py-12 px-4 text-center space-y-4">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                      <Share2 className="h-6 w-6" />
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Pick a list to share</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Choose a list from your dashboard to share it.</p>
                    </div>
                    <button type="button" onClick={handleClose} className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-slate-700 dark:text-gray-100 dark:hover:bg-slate-800">Back to lists</button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
